/*
 * =====================================================================
 * 작 업 명  : 전역함수
 * 파 일 명  : global.js
 * 변경이력
 * 2022-06-17 / 정태화 / 최초작성
 * 2023-02-10 / 이성준 / 업체번호 전역변수 추가
 * 2023-10-27 / 이성준 / 유저 권한 스키마 변경 dot-saas-vision ➡️ dot-saas-user
 * =====================================================================
 */
import { $session, $router, $site } from "dot-framework"
import store from "@/store"

/*
 * 업체번호는 도메인으로 물어봐야 알 수 있는데, 화면들은 그 값으로 API 경로를 만든다.
 * 조회가 끝나기 전에 화면이 먼저 요청을 쏘면 경로에서 업체번호 자리가 비어
 * (`/sites/S220727001-/users/...`) 서버가 412 를 돌려준다. 그래서 준비될 때까지
 * 기다릴 수 있도록 약속을 하나 내어 둔다.
 *
 * 이 약속은 무슨 일이 있어도 풀린다. 여기서 멈추면 화면이 영영 뜨지 않기 때문에,
 * 조회가 실패하거나 너무 늦으면 예전처럼 업체번호 없이 그냥 진행한다.
 */
const COMP_NO_TIMEOUT_MS = 8000

let releaseCompNo = null
let compNoReleased = false
const compNoReady = new Promise(resolve => { releaseCompNo = resolve })

const markCompNoReady = () => {
    if (compNoReleased) return
    compNoReleased = true
    releaseCompNo()
}

// setSession 이 불리지 않는 경로에서도 기다림이 남지 않도록 한 번 더 막아 둔다.
setTimeout(markCompNoReady, COMP_NO_TIMEOUT_MS)

/** 업체번호가 준비될 때까지(또는 포기할 때까지) 기다린다. */
export function whenCompNoReady() {
    return compNoReady
}

export default {
    // 통합인증 및 메뉴/권한 설정
    setSession() {
        // (1) 통합인증 연동 설정
        let siteNo = process.env.VUE_APP_SITE_NO
        let ssoDomain = process.env.VUE_APP_SSO_DOMAIN
        let callbackUrl = process.env.VUE_APP_CALLBACK_URL
        
        $session.setSiteNo(siteNo)
        $session.setSsoDomain(ssoDomain)
        $session.setCallbackUrl(callbackUrl)

        // (2) 업체번호 설정 및 메뉴/권한 연동
        if (process.env.VUE_APP_IS_ELECTRON === "true") {
            const compNo = "C220512001" // 업체번호(주식회사닷) 설정
            $session.setCompNo(compNo)
            store.commit("setCompNo", compNo)
            markCompNoReady()
            store.dispatch("setMenuList") // 메뉴/권한 연동
       } else {
            // 도메인에서 업체번호 조회
            let domain = $router.getDomain()

            // localhost 대응
            const localIp = "127.0.0.1"
            const localPort = "9070"
            if (domain.includes("localhost")) {
                domain = domain.replace("localhost", localIp)
            }

            const url = domain.split(":")
            const ip = url[0]
            const port = url[1]
            if (ip === localIp && port !== localPort) {
                domain = `${ip}:${localPort}`
            }

            $site.setCompNoBydomain(domain, function(compNo) {
                $session.setCompNo(compNo) // 업체번호 설정
                store.commit("setCompNo", compNo)
                // 경로를 만들 수 있게 된 시점에 바로 풀어 준다.
                // 메뉴·권한은 화면이 기다릴 이유가 없으므로 뒤로 보낸다.
                markCompNoReady()
                store.dispatch("setMenuList") // 메뉴/권한 연동
            })
                .catch(() => {
                    // 업체번호를 못 받아도 화면은 떠야 한다.
                    markCompNoReady()
                })
       }
    },
    isLogin() {
        return $session.isLogin()
    },
    isDotAdmin() {
        const roles = $session.getUserRoles('dot-saas-user')
        return !!(roles && roles.length && roles.includes('DOT-ADMIN'))
    },
    isDotUser() {
        const roles = $session.getUserRoles('dot-saas-user')
        return !!(roles && roles.length && roles.includes('DOT-USER'))
    },
    isKCHFUser() {
      const userId = $session.getUserInfo().USER_ID
      return userId === 'kchf'
    }
}
